import { useCallback, useEffect } from "react";

/**
 * Clears the `pointer-events: none` Radix can leave on `<body>` after a dialog closes.
 *
 * Radix sets it while a modal is open and removes it on cleanup. When a close
 * and a re-render land in the same tick — a confirm handler that advances a
 * step, refetches, or navigates — that cleanup is interrupted and the style
 * stays, freezing the whole page behind the dialog that is no longer there.
 *
 * The check for another open dialog matters: stacked dialogs share the style,
 * and clearing it while one is still open would let clicks through to the page
 * underneath.
 *
 * @param params.isOpen - Whether the dialog is open, or undefined when it manages its own state
 * @returns A close handler to pass to the dialog, wrapping the caller's own
 */
export const useDialogBodyPointerEventsReset = ({ isOpen }: { isOpen?: boolean }): { onClosed: () => void } => {
  const onClosed = useCallback(() => {
    requestAnimationFrame(() => {
      const anotherDialogOpen = document.querySelector(
        '[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"]'
      );
      if (!anotherDialogOpen && document.body.style.pointerEvents === "none") {
        document.body.style.pointerEvents = "";
      }
    });
  }, []);

  // A parent that closes the dialog by flipping its `open` prop never routes
  // through the dialog's own close handler, so the prop is watched too. Only an
  // explicit `false` counts: a dialog left to manage its own state passes
  // undefined, and treating that as closed would reset on every render of one
  // that was never opened from outside.
  useEffect(() => {
    if (isOpen === false) {
      onClosed();
    }
  }, [isOpen, onClosed]);

  return { onClosed: onClosed };
};
